<template>
  <div>
    <SiteBanner :site-address="siteAddress" :site-name="siteName" />
    <WorkOrderDetails v-if="hasSelectedWorkOrder" class="mt-4" />
    <div class="mb-20 ml-auto mr-auto mt-4 w-11/12">
      <FormGenerator
        :key="formGeneratorKey"
        :schema="schema"
        @form-changed="formChangedEventHandler"
        @visibility-changed="updateVisibleComponentsEventHandler"
        @update-component-visibility="updateComponentVisibilityEventHandler"
        @persist-photo="storePhotoEventHandler"
        @remove-photo="removePhotoEventHandler"
        @document-uploaded="documentUploadedEventHandler"
        @document-removed="documentRemovedEventHandler"
      />
      <PrimaryButton
        :key="submitButtonKey"
        :disabled="isLoading"
        action="Submit"
        class="mt-4"
        type="submit"
        @click.once="submitForm"
      />
    </div>
    <LoadingView :is-loading="isLoading" :message="loadingMessage" />
    <PopulatePermitDataConfirmation
      :show="shouldPopulateSavedFormData"
      @populate="populatePermitFormData"
      @remove="clearPermitAnswers"
    />
    <ScrollToTop />
  </div>
</template>

<script lang="ts" setup>
  import SiteBanner from '@/components/SiteBanner.vue'
  import {
    Answer,
    checkAnswers,
    errorMessage,
    FormDocument,
    FormGenerator,
    ImageAnswer,
    removePhoto,
    SchemaType,
    scrollToFirstError,
    storePhoto,
    updateComponentVisibility,
    updateVisibleComponents,
    validateFormSubmission,
  } from '@verifiedit/form-generator'
  import PrimaryButton from '@/components/PrimaryButton.vue'
  import { computed, nextTick, reactive, ref } from 'vue'
  import InvalidStoreException from '@/exceptions/InvalidStoreException'
  import { getImages, removeImage, storeImage } from '@/services/api/image'
  import { useRoute, useRouter } from 'vue-router'
  import {
    fetchPermitForms,
    PermitForm,
    submitEndPermitForm,
    submitPermitForm,
  } from '@/services/api/permit'
  import { deleteDocument, storeDocument } from '@/services/api/document'
  import LoadingView from '@/views/LoadingView.vue'
  import PopulatePermitDataConfirmation from '@/components/PopulatePermitDataConfirmation.vue'
  import { useUserLogin } from '@/storage/userLogin'
  import { storeToRefs } from 'pinia'
  import { useCustomerStore } from '@/storage/customer'
  import { usePermitFormStore } from '@/storage/permitForm'
  import { useSiteStore } from '@/storage/site'
  import { useWorkOrderStore } from '@/storage/workOrder'
  import { useUserStore } from '@/storage/user'
  import WorkOrderDetails from '@/components/WorkOrderDetails.vue'
  import { useUserVisitorCompaniesStore } from '@/storage/userVisitorCompanies'
  import { usePermitFormSubmissionsStore } from '@/storage/permitFormSubmissions'
  import ScrollToTop from '@/components/ScrollToTop.vue'
  import { getValidSupplementalFormsToAdd } from '@/utilities/supplementalForm'
  import objectUrls from '@/components/use/objectUrls'
  import { useSupplementalFormStore } from '@/storage/supplementalForms'

  const { isVisitor } = storeToRefs(useUserLogin())
  const { setSupplementalForms } = useSupplementalFormStore()
  const {
    address: siteAddress,
    name: siteName,
    id: siteId,
    isSiteStoreDefault,
  } = storeToRefs(useSiteStore())
  const { id: customerId, isCustomerStoreDefault } =
    storeToRefs(useCustomerStore())
  const {
    id: userId,
    userType,
    isUserStoreDefault,
    isUserTypeDefault,
    selectedContractorCompanyName,
    selectedContractorCompanyId,
  } = storeToRefs(useUserStore())
  const { lastSelected } = storeToRefs(useUserVisitorCompaniesStore())
  const {
    setStartPermit,
    setEndPermit,
    setStartPermitSupplementals,
    setEndPermitId,
    setStartPermitId,
    setPermitAnswers,
  } = usePermitFormStore()
  const {
    endPermit,
    getPermitAnswers,
    startPermit,
    startPermitId,
    endPermitId,
    hasEndPermit,
    hasSubmittedStartPermit,
    startPermitSupplementals,
  } = storeToRefs(usePermitFormStore())
  const { hasSelectedWorkOrder } = storeToRefs(useWorkOrderStore())
  const { populateWorkOrderData } = useWorkOrderStore()
  const { setId } = usePermitFormSubmissionsStore()

  const router = useRouter()
  const schema = ref<(SchemaType & errorMessage)[]>([])
  const answers: Map<string, Answer> = reactive(new Map())
  const visibleComponents = ref<string[]>([])
  const submitButtonKey = ref<number>(0)
  const isLoading = ref<boolean>(true)
  const loadingMessage = ref<string>('Loading permit form...')
  const isEndPermit = useRoute().path === '/permit-form/end'
  const savedAnswers = getPermitAnswers.value

  const shouldPopulateSavedFormData = computed(
    () =>
      schema.value.length !== 0 &&
      hasSubmittedStartPermit.value &&
      !isEndPermit,
  )
  const formGeneratorKey = ref(0)
  const { releaseObjectUrl, trackObjectUrl } = objectUrls()

  try {
    schema.value = await fetchForms()
    populateWorkOrderData(schema.value as (SchemaType & errorMessage)[])
    isLoading.value = false
  } catch (e) {
    isLoading.value = false
    throw e
  }

  async function populateSavedDataOnForm() {
    if (!shouldPopulateSavedFormData.value) {
      return
    }

    for (let i = 0; i < schema.value.length; i++) {
      const component = schema.value[i]
      const answer = savedAnswers.get(component.identifier)
      if (answer !== undefined) {
        if (component.component === 'PhotoInput') {
          isLoading.value = true
          component.photos = await getImages(answer as number[], trackObjectUrl)
          isLoading.value = false
        } else {
          component.default = answer
        }
      }
      component.errorMessage = ''
    }
  }

  async function fetchForms(): Promise<(SchemaType & errorMessage)[]> {
    if (isEndPermit && hasEndPermit.value) {
      return deepClone(endPermit.value)
    } else if (startPermit.value.length !== 0) {
      return deepClone(startPermit.value)
    }

    if (isCustomerStoreDefault.value || isUserTypeDefault.value) {
      throw new InvalidStoreException()
    }

    const forms = await fetchPermitForms(customerId.value, userType.value.id)

    if (forms.length === 0) {
      await routeToNext()
    }

    savePermitForms(forms)

    return deepClone(startPermit.value)
  }

  function savePermitForms(forms: PermitForm[]) {
    forms.reduce(
      (accumulator, permit) => {
        if (permit.type === 'START') {
          accumulator.start = permit.form.template.components
          setStartPermitId(permit.id)
          setStartPermit(permit.form.template.components)
          setStartPermitSupplementals(permit.form.template.supplementals ?? [])

          return accumulator
        }
        accumulator.end = permit.form.template.components
        setEndPermitId(permit.id)
        setEndPermit(permit.form.template.components)

        return accumulator
      },
      { end: [] as SchemaType[], start: [] as SchemaType[] },
    )
  }

  function formChangedEventHandler(
    changedComponentName: string,
    userInput: string | (string | ImageAnswer)[],
  ) {
    checkAnswers(
      changedComponentName,
      userInput,
      schema.value as (SchemaType & errorMessage)[],
      answers,
    )
  }

  function updateVisibleComponentsEventHandler(
    components: Map<string, string>,
  ) {
    visibleComponents.value = updateVisibleComponents(components, answers)
  }

  function updateComponentVisibilityEventHandler(
    identifier: string,
    result: { items: string[]; value: boolean },
    key: string,
  ) {
    updateComponentVisibility(
      identifier,
      result,
      key,
      schema.value as (SchemaType & errorMessage)[],
    )
  }

  async function storePhotoEventHandler(
    imageData: string,
    imageTypeId: number,
    fileName: string | null,
    identifier: string,
  ) {
    if (
      isCustomerStoreDefault.value ||
      isSiteStoreDefault.value ||
      isUserStoreDefault.value
    ) {
      throw new InvalidStoreException()
    }

    loadingMessage.value = 'Saving image...'
    isLoading.value = true
    await storePhoto(
      imageData,
      imageTypeId,
      fileName,
      identifier,
      schema.value as (SchemaType & errorMessage)[],
      () =>
        storeImage(
          customerId.value,
          siteId.value,
          userId.value,
          imageData,
          imageTypeId,
          fileName,
        ),
    )
    isLoading.value = false
  }

  async function removePhotoEventHandler(imageId: string, identifier: string) {
    loadingMessage.value = 'Removing image...'
    isLoading.value = true
    await removePhoto(
      imageId,
      identifier,
      schema.value as (SchemaType & errorMessage)[],
      answers,
      removeImage,
    )
    isLoading.value = false
  }

  async function routeToNext() {
    if (isVisitor.value) {
      await router.replace({ name: 'readyToCheckout' })
    } else {
      await router.replace({ name: 'serviceSelection' })
    }
  }

  function savePermitFormDataToStore() {
    if (answers.has('workOrder')) {
      answers.delete('workOrder')
    }

    if (answers.has('workDescription')) {
      answers.delete('workDescription')
    }

    setPermitAnswers(Object.fromEntries(answers))
  }

  async function submitForm() {
    submitButtonKey.value++

    const canSubmit = validateFormSubmission(
      schema.value as (SchemaType & errorMessage)[],
      answers,
      visibleComponents.value,
    )
    if (!canSubmit) {
      await nextTick()
      scrollToFirstError()
      return
    }

    const currentPermitId = isEndPermit
      ? endPermitId.value
      : startPermitId.value

    if (
      isCustomerStoreDefault.value ||
      isSiteStoreDefault.value ||
      currentPermitId === null
    ) {
      throw new InvalidStoreException()
    }

    loadingMessage.value = 'Saving permit...'
    isLoading.value = true

    if (isEndPermit) {
      await submitEndPermitForm(currentPermitId, answers)

      setEndPermit([])
      await router.replace({ name: 'checkout' })
      return
    }

    const contractorCompanyName = selectedContractorCompanyName.value
      ? selectedContractorCompanyName.value
      : lastSelected.value

    const response = await submitPermitForm(
      selectedContractorCompanyId.value,
      contractorCompanyName,
      customerId.value,
      siteId.value,
      currentPermitId,
      answers,
      endPermitId.value,
    )
    setId(response.id)

    setSupplementalForms(
      'startPermit',
      getValidSupplementalFormsToAdd(
        startPermitSupplementals.value ?? [],
        answers,
      ),
      {
        startPermitId: response.id,
      },
    )

    savePermitFormDataToStore()
    await routeToNext()
    isLoading.value = false
  }

  function deepClone(object: SchemaType[]) {
    return JSON.parse(JSON.stringify(object))
  }

  async function populatePermitFormData() {
    await populateSavedDataOnForm()
    formGeneratorKey.value++
  }

  function clearPermitAnswers() {
    setPermitAnswers({})
  }

  async function documentUploadedEventHandler(
    identifier: string,
    document: File,
  ) {
    isLoading.value = true
    loadingMessage.value = 'Saving document...'

    const documentComponent = schema.value.find(
      (component) => component.identifier === identifier,
    )

    if (!documentComponent) {
      return
    }

    try {
      let documentType: string | null = null

      if (
        Object.prototype.hasOwnProperty.call(documentComponent, 'documentType')
      ) {
        documentType = documentComponent['documentType'] as string | null
      }

      const response = await storeDocument(
        document,
        document.name,
        document.type,
        documentType,
      )

      // Previewed from the file just picked, not from the url the api answered with: those
      // bytes are in quarantine until the scan clears them, and the browser already holds a
      // copy. Revoking the url this makes is this view's job (see `documentRemovedEventHandler`
      // and `objectUrls`), because the library only renders what it is given.
      const uploadedDocument: FormDocument = {
        // What the api detected from the bytes, not what the browser guessed from the name.
        mimeType: response.mimeType || document.type,
        name: document.name,
        preview: trackObjectUrl(URL.createObjectURL(document)),
        status: response.status,
        uuid: response.uuid,
      }

      if (documentComponent.documents) {
        documentComponent.documents.push(uploadedDocument)
      } else {
        documentComponent.documents = [uploadedDocument]
      }
    } catch (error) {
      console.error('Error uploading document:', error)
      throw error
    } finally {
      isLoading.value = false
      loadingMessage.value = 'Loading...'
    }
  }

  async function documentRemovedEventHandler(
    identifier: string,
    documentUuid: string,
  ) {
    isLoading.value = true
    loadingMessage.value = 'Removing document...'

    try {
      const deleted = await deleteDocument(documentUuid)

      if (!deleted) {
        console.error('Error deleting document with uuid:', documentUuid)
        return
      }

      const documentComponent = schema.value.find(
        (component) => component.identifier === identifier,
      )

      if (documentComponent && documentComponent.documents) {
        const removed = documentComponent.documents.find(
          (document) => document.uuid === documentUuid,
        )

        if (removed?.preview) {
          releaseObjectUrl(removed.preview)
        }

        documentComponent.documents = documentComponent.documents.filter(
          (document) => document.uuid !== documentUuid,
        )
      }
    } catch (error) {
      console.error('Error removing document:', error)
      throw error
    } finally {
      isLoading.value = false
      loadingMessage.value = 'Loading...'
    }
  }
</script>
